<template>
    <div class="results-block mb-3">
        <b-card class="mb-1">
            <div class="d-flex filters p-0 p-md-3">
                <fa-icon icon="calendar-alt" class="mr-2 align-self-center"></fa-icon>
                <datepicker v-model="date"
                    :monday-first="true"
                    :bootstrap-styling="true"
                    format="yyyy-MM-dd">
                </datepicker>
                <b-form-select v-model="selectedGameId" :options="gamesOptions"></b-form-select>
                <div class="loading">
                    <b-spinner v-show="isLoading"></b-spinner>
                </div>
            </div>
        </b-card>
        <b-card>
            <div class="p-0 p-md-3">
                <b-table
                    striped
                    hover
                    class="mb-0"
                    stacked="lg"
                    head-variant="light"
                    ref="table"
                    :per-page="perPage"
                    :current-page="page"
                    :busy.sync="isLoading"
                    :items="fetch"
                    :fields="fields">
                    <template slot="cell(game_name)" slot-scope="{ item }">
                        <span>{{ item.game_name | lang('titles') }}</span>
                    </template>
                    <template slot="cell(video_url)" slot-scope="{ item }">
                        <b-link :href="item.video_url" target="_blank">{{ 'watch' | lang }}</b-link>
                    </template>
                    <template slot="cell(selection)" slot-scope="{ item }">
                        <div class="symbols d-flex flex-wrap" :class="`symbols-${item.game_name}`">
                            <span class="symbol mr-1 mb-1" :class="isGuessed(item, symbol, index) ? 'active' : ''" v-for="(symbol, index) in item.selection" :key="index">{{ symbol }}</span>
                        </div>
                    </template>
                    <template slot="cell(results)" slot-scope="{ item }">
                        <div v-if="item.returned"><strong class="text-warning">{{ 'draw_cancelled' | lang }}</strong></div>
                        <div v-else class="symbols d-flex flex-wrap" :class="`symbols-${item.game_name}`">
                            <span class="symbol mr-1 mb-1" v-for="(symbol, index) in item.results" :key="index">{{ symbol }}</span>
                        </div>
                    </template>
                    <template slot="cell(barcode)" slot-scope="{ item }">
                        <b-link v-if="item.has_combinations" @click="showCombinations(item.ticket_id)">{{ item.barcode }}</b-link>
                        <span v-else>{{ item.barcode }}</span>
                        <span class="alternative-lottery-barcode" :class="`game-${item.game_name}`">{{ item.barcode_alt }}</span>
                    </template>
                    <template slot="cell(amount_payout)" slot-scope="{ item }">
                        <span v-if="!item.amount_payout">-</span>
                        <span v-else-if="item.status == 'won'" class="text-success">{{ item.amount_payout }}</span>
                        <span v-else-if="item.status == 'lost'" class="text-danger">{{ item.amount_payout }}</span>
                    </template>
                </b-table>
            </div>
        </b-card>
        <b-card>
            <b-pagination
                v-model="page"
                :total-rows="totalCount"
                :per-page="perPage">
            </b-pagination>
        </b-card>
        <b-modal v-model="showCombinationsModal" :hide-footer="true">
            <combinations :ticket-id="combinationsTicketId"></combinations>
        </b-modal>
    </div>
</template>

<script>
import Axios from "axios";
import moment from "moment";
import Datepicker from 'vuejs-datepicker';
import currency from '@tv/filters/currency';
import lang from '@tv/filters/lang';
import datetime from '@lc/filters/datetime';
import Combinations from './Combinations';
import { mapState } from 'vuex';
import { head } from 'lodash';

import { syncFrameHeight } from "@tv/helpers";

export default {
    props: ['mode'],

    components: { Datepicker, Combinations },

    data() {
        const fields = [];

        if (this.mode == 'results') {
            fields.push(
                { key: 'draw_time', label: lang('time') },
                { key: 'draw_code', label: lang('draw') },
                { key: 'results', label: lang('results') },
                { key: 'video_url', label: lang('video') },
            );
        } else if (this.mode == 'tickets') {
            fields.push(
                { key: 'game_name', label: lang('game') },
                { key: 'datetime_payin', label: lang('date') },
                { key: 'draw_code', label: lang('draw') },
                { key: 'barcode', label: lang('barcode') },
                { key: 'selection', label: lang('choice') },
                { key: 'results', label: lang('results') },
                { key: 'amount', label: lang('amount') },
                { key: 'amount_payout', label: lang('win') },
            );
        }

        return {
            date: new Date,
            selectedGameId: this.$route.params.gameId || Config.game_id,
            isLoading: false,
            page: 1,
            perPage: 10,
            totalCount: 0,
            fields,

            showCombinationsModal: false,
            combinationsTicketId: 0,
        };
    },

    computed: {
        gamesOptions() {
            const options = [ { value: 0, text: lang('all_games') } ];

            for (const [ gameId, { name, version } ] of Object.entries(Config.games)) {
              if (version === 2) {
                options.push({ value: gameId, text: lang(`titles.${name}`) });
              }
            }

            return options;
        },
    },
    
    methods: {
        head,

        async fetch() {
            try {
                const params = {
                    mode: this.mode,
                    date: moment(this.date).format('YYYY-MM-DD'),
                    skip: (this.page - 1) * this.perPage,
                    game_id: this.selectedGameId,
                };

                const { data: res } = await Axios.get(Config.route.history, { params });

                this.totalCount = res.total;
                
                return res.list;

            } catch (err) {
                console.error(err);
            }
        },

        showCombinations(ticketId) {
            this.combinationsTicketId = ticketId;
            this.showCombinationsModal = true;
        },

        isGuessed(item, symbol, index) {
            switch (item.game_name) {
                case 'Jokerjackpot':
                    return item.results[index] == symbol;
                default:
                    return item.results.includes(symbol);
            }
        },
    },

    watch: {
        date() {
            this.page = 1;
            this.$refs.table.refresh();
        },

        selectedGameId() {
            this.page = 1;
            this.$refs.table.refresh();
        },

        isLoading() {
            syncFrameHeight();
        },
    },
}
</script>
